export type AIMediaKind = 'image' | 'video' | 'voice'
const limits = { image: 20 * 1024 * 1024, video: 100 * 1024 * 1024, voice: 20 * 1024 * 1024 }

function error(code: string): Error { return Object.assign(new Error(code), { code }) }
export function validateAIMediaUrl(value: string): URL {
  const url = new URL(value)
  if (url.protocol !== 'https:' || url.username || url.password || value.length > 8192) throw error('MEDIA_URL_INVALID')
  return url
}

export async function downloadAIMedia(url: string, kind: AIMediaKind, fetcher: typeof fetch = fetch): Promise<Blob> {
  validateAIMediaUrl(url)
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 60000)
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined
  try {
    const response = await fetcher(url, { signal: controller.signal, credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer' })
    if (!response.ok) throw error('MEDIA_DOWNLOAD_FAILED')
    const mime = (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase()
    const valid = kind === 'image' ? ['image/jpeg', 'image/png', 'image/webp'].includes(mime)
      : kind === 'video' ? mime === 'video/mp4' : ['audio/ogg', 'application/ogg'].includes(mime)
    if (!valid) throw error('MEDIA_TYPE_INVALID')
    const limit = limits[kind], declared = Number(response.headers.get('content-length'))
    if (declared > limit) throw error('MEDIA_TOO_LARGE')
    const chunks: Uint8Array[] = []; let size = 0
    if (response.body?.getReader) {
      reader = response.body.getReader()
      while (true) {
        const part = await reader.read()
        if (part.done) break
        size += part.value.byteLength
        if (size > limit) throw error('MEDIA_TOO_LARGE')
        chunks.push(part.value)
      }
    } else {
      const buffer = await response.arrayBuffer(); size = buffer.byteLength
      if (size > limit) throw error('MEDIA_TOO_LARGE')
      chunks.push(new Uint8Array(buffer))
    }
    if (!size) throw error('MEDIA_EMPTY')
    // OGG alone is not a codec guarantee: voice input must actually contain an Opus header.
    if (kind === 'voice') {
      const head = new Uint8Array(await new Blob(chunks).slice(0, 512).arrayBuffer())
      const ascii = String.fromCharCode(...head)
      if (!ascii.startsWith('OggS') || !ascii.includes('OpusHead')) throw error('VOICE_OPUS_REQUIRED')
    }
    return new Blob(chunks, { type: kind === 'voice' ? 'audio/ogg' : mime })
  } catch (e: any) {
    if (typeof e?.code === 'string' && /^(MEDIA_|VOICE_)/.test(e.code)) throw e
    throw error(controller.signal.aborted ? 'MEDIA_DOWNLOAD_TIMEOUT' : 'MEDIA_DOWNLOAD_FAILED')
  } finally {
    clearTimeout(timer)
    controller.abort()
    try { await reader?.cancel() } catch { /* A consumed or aborted stream is already closed. */ }
  }
}

export async function prepareAIMedia(url: string, kind: AIMediaKind) {
  const file = await downloadAIMedia(url, kind)
  const objectURL = URL.createObjectURL(file)
  const element = document.createElement(kind === 'image' ? 'img' : kind === 'video' ? 'video' : 'audio') as HTMLImageElement & HTMLMediaElement & HTMLVideoElement
  let disposed = false
  const dispose = () => {
    if (disposed) return
    disposed = true
    element.onload = element.onerror = element.onloadedmetadata = null
    element.removeAttribute('src')
    URL.revokeObjectURL(objectURL)
  }
  try {
    const metadata = await new Promise<{ width?: number; height?: number; duration?: number }>((resolve, reject) => {
      const timer = setTimeout(() => { cleanup(); reject(error('MEDIA_METADATA_TIMEOUT')) }, 15000)
      const cleanup = () => { clearTimeout(timer); element.onload = element.onerror = element.onloadedmetadata = null }
      element.onerror = () => { cleanup(); reject(error('MEDIA_DECODE_FAILED')) }
      const ready = () => {
        const width = kind === 'image' ? element.naturalWidth : kind === 'video' ? element.videoWidth : undefined
        const height = kind === 'image' ? element.naturalHeight : kind === 'video' ? element.videoHeight : undefined
        const duration = kind === 'image' ? undefined : element.duration
        cleanup()
        if ((kind !== 'voice' && (!(width > 0) || !(height > 0))) ||
            (kind !== 'image' && (!Number.isFinite(duration) || !(duration > 0)))) {
          reject(error('MEDIA_METADATA_INVALID')); return
        }
        resolve({ width, height, ...(duration ? { duration: Math.ceil(duration) } : {}) })
      }
      if (kind === 'image') element.onload = ready
      else { element.preload = 'metadata'; element.onloadedmetadata = ready }
      element.src = objectURL
    })
    return { file, objectURL, ...metadata, dispose }
  } catch (e) { dispose(); throw e }
}
